import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { db } from './firebase';
import { collection, getDocs } from 'firebase/firestore';
import './App.css';

// Components
import Header from './components/Header';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import StudentView from './components/StudentView';
import StaffView from './components/StaffView';
import NonRespondersView from './components/NonRespondersView';
import ExportView from './components/ExportView';
import ProgramImportTool from './components/ProgramImportTool';
import LoadingSpinner from './components/LoadingSpinner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [studentRsvps, setStudentRsvps] = useState([]);
  const [staff, setStaff] = useState([]);
  const [staffRsvps, setStaffRsvps] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const auth = getAuth();

  // Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  // Fetch data when authenticated
  useEffect(() => {
    if (!user) return;
    
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch all students
        const studentsSnapshot = await getDocs(collection(db, "students"));
        const studentsData = studentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStudents(studentsData);
        
        // Fetch all student RSVPs
        const rsvpsSnapshot = await getDocs(collection(db, "rsvps"));
        const rsvpsData = rsvpsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStudentRsvps(rsvpsData);
        
        // Fetch all staff
        const staffSnapshot = await getDocs(collection(db, "staff"));
        const staffData = staffSnapshot.docs.map(doc => ({
          username: doc.id,
          ...doc.data()
        }));
        setStaff(staffData);
        
        // Fetch all staff RSVPs
        const staffRsvpsSnapshot = await getDocs(collection(db, "staffRsvps"));
        const staffRsvpsData = staffRsvpsSnapshot.docs.map(doc => ({
          username: doc.id,
          ...doc.data()
        }));
        setStaffRsvps(staffRsvpsData);
        
        setLastUpdateTime(new Date());
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please refresh the page or contact support.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
    
    // Set up a refresh interval (every 5 minutes)
    const intervalId = setInterval(fetchAllData, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [user]);

  const handleLogin = async (email, password) => {
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error("Login error:", error);
      setError("Invalid login credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleRefreshData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all students
      const studentsSnapshot = await getDocs(collection(db, "students"));
      const studentsData = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudents(studentsData);
      
      // Fetch all student RSVPs
      const rsvpsSnapshot = await getDocs(collection(db, "rsvps"));
      const rsvpsData = rsvpsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudentRsvps(rsvpsData);
      
      // Fetch all staff
      const staffSnapshot = await getDocs(collection(db, "staff"));
      const staffData = staffSnapshot.docs.map(doc => ({
        username: doc.id,
        ...doc.data()
      }));
      setStaff(staffData);
      
      // Fetch all staff RSVPs
      const staffRsvpsSnapshot = await getDocs(collection(db, "staffRsvps"));
      const staffRsvpsData = staffRsvpsSnapshot.docs.map(doc => ({
        username: doc.id,
        ...doc.data()
      }));
      setStaffRsvps(staffRsvpsData);
      
      setLastUpdateTime(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to refresh data. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  // Calculate respondents and non-respondents
  const studentRespondents = studentRsvps.filter(rsvp => 
    rsvp.status === "confirmed" || rsvp.completedSteps?.includes("confirmation")
  );
  
  const staffRespondents = staffRsvps.filter(rsvp => 
    rsvp.status === "confirmed" || rsvp.completedSteps?.includes("confirmation")
  );
  
  const studentNonRespondents = students.filter(student => 
    !studentRespondents.some(rsvp => rsvp.id === student.id || rsvp.studentId === student.id)
  );
  
  const staffNonRespondents = staff.filter(staffMember => 
    !staffRespondents.some(rsvp => rsvp.username === staffMember.username || rsvp.staffUsername === staffMember.username)
  );

  if (loading && !user) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <div className="admin-app">
        <Header isAdmin={true} user={user} onLogout={handleLogout} />
        
        {error && (
          <div className="error-banner">
            <p>{error}</p>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}
        
        <div className="admin-container">
          {!user ? (
            <Login onLogin={handleLogin} error={error} />
          ) : (
            <>
              <div className="admin-sidebar">
                <nav>
                  <ul>
                    <li>
                      <a href="/" onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/';
                      }}>
                        Dashboard
                      </a>
                    </li>
                    <li>
                      <a href="/students" onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/students';
                      }}>
                        Student RSVPs ({studentRespondents.length}/{students.length})
                      </a>
                    </li>
                    <li>
                      <a href="/staff" onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/staff';
                      }}>
                        Staff RSVPs ({staffRespondents.length}/{staff.length})
                      </a>
                    </li>
                    <li>
                      <a href="/non-responders" onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/non-responders';
                      }}>
                        Non-Responders ({studentNonRespondents.length + staffNonRespondents.length})
                      </a>
                    </li>
                    <li>
                      <a href="/export" onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/export';
                      }}>
                        Export Data
                      </a>
                    </li>
                    <li>
                      <a href="/programs-import" onClick={(e) => {
                        e.preventDefault();
                        window.location.href = '/programs-import';
                      }}>
                        Import Programs
                      </a>
                    </li>
                  </ul>
                </nav>
                
                <div className="refresh-section">
                  <button 
                    onClick={handleRefreshData} 
                    disabled={loading}
                    className="refresh-button"
                  >
                    {loading ? 'Refreshing...' : 'Refresh Data'}
                  </button>
                  {lastUpdateTime && (
                    <p className="last-updated">
                      Last updated: {lastUpdateTime.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="admin-content">
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <Routes>
                    <Route 
                      path="/" 
                      element={
                        <Dashboard 
                          students={students}
                          studentRsvps={studentRsvps}
                          staff={staff}
                          staffRsvps={staffRsvps}
                          studentRespondents={studentRespondents}
                          staffRespondents={staffRespondents}
                          studentNonRespondents={studentNonRespondents}
                          staffNonRespondents={staffNonRespondents}
                        />
                      } 
                    />
                    <Route 
                      path="/students" 
                      element={
                        <StudentView 
                          students={students}
                          studentRsvps={studentRsvps}
                        />
                      } 
                    />
                    <Route 
                      path="/staff" 
                      element={
                        <StaffView 
                          staff={staff}
                          staffRsvps={staffRsvps}
                        />
                      } 
                    />
                    <Route 
                      path="/non-responders" 
                      element={
                        <NonRespondersView 
                          studentNonRespondents={studentNonRespondents}
                          staffNonRespondents={staffNonRespondents}
                        />
                      } 
                    />
                    <Route 
                      path="/export" 
                      element={
                        <ExportView 
                          students={students}
                          studentRsvps={studentRsvps}
                          staff={staff}
                          staffRsvps={staffRsvps}
                          studentNonRespondents={studentNonRespondents}
                          staffNonRespondents={staffNonRespondents}
                        />
                      } 
                    />
                    <Route 
                      path="/programs-import" 
                      element={<ProgramImportTool />} 
                    />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                )}
              </div>
            </>
          )}
        </div>
        
        <footer className="admin-footer">
          <p>Veterans Memorial High School · CTE Senior Awards Ceremony · May 4, 2026 · Admin Dashboard</p>
        </footer>
      </div>
    </Router>
  );
}

export default App;